import type { ApiErrorCode } from '../../shared/errors.js';
import { API_ERROR_LABELS } from '../../shared/errors.js';

/**
 * The browser's side of the API.
 *
 * Same origin, so there is no base URL to configure and no CORS to negotiate.
 * The session travels in an HttpOnly cookie the script cannot read; the CSRF
 * token is held in React state and attached here.
 *
 * Nothing is written to `localStorage` or `sessionStorage`, not the session, not
 * the CSRF token, and not a Query Lab result. Browser storage survives the tab,
 * survives logout, and is readable by any script that ends up on the origin —
 * all three are exactly what the security contract rules out.
 */

export class ApiRequestError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    readonly status: number,
    readonly retryAfterSeconds?: number,
  ) {
    super(API_ERROR_LABELS[code]);
    this.name = 'ApiRequestError';
  }
}

export const CSRF_HEADER = 'X-Dashboard-CSRF';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  csrfToken?: string | undefined;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = new Headers({ accept: 'application/json' });
  if (options.body !== undefined) headers.set('content-type', 'application/json');
  if (options.csrfToken !== undefined) headers.set(CSRF_HEADER, options.csrfToken);

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      // Same-origin only. `omit` would drop the session cookie; `include` would
      // be a promise this app never wants to be able to keep.
      credentials: 'same-origin',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch {
    // A fetch rejection here means the dashboard process itself is gone. There
    // is no message worth surfacing; the code is the whole story.
    throw new ApiRequestError('internal_error', 0);
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    (Record<string, unknown> & { error?: string; retryAfterSeconds?: number }) | null;

  if (!response.ok) {
    const code = isApiErrorCode(payload?.error) ? payload.error : 'internal_error';
    throw new ApiRequestError(
      code,
      response.status,
      typeof payload?.retryAfterSeconds === 'number' ? payload.retryAfterSeconds : undefined,
    );
  }
  return payload as T;
}

function isApiErrorCode(value: unknown): value is ApiErrorCode {
  return typeof value === 'string' && value in API_ERROR_LABELS;
}
